import React, { ReactNode, useCallback, useEffect, useState } from "react";

import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import DateRangeType from "@mapmycustomers/shared/enum/DateRangeType";
import DateRange, {
  StandardAndCustomRangeType,
} from "@mapmycustomers/shared/types/range/DateRange";
import { Button } from "@mapmycustomers/ui";

import { Range, RangeTypeSelect } from "@app/component/input/dateRangeSelect";
import layout from "@app/styles/layout";
import useAnalytics from "@app/util/contexts/useAnalytics";
import parseRange from "@app/util/range/parseRange";
import standardRangeToDateRange from "@app/util/range/standardRangeToDateRange";

import styles from "./DateRangeSelector.module.scss";

interface Props {
  children: ReactNode;
  dateRange?: DateRange;
  dateRangeType: StandardAndCustomRangeType;
  onChange?: (payload: { dateRange: DateRange; dateRangeType: StandardAndCustomRangeType }) => void;
}

const DateRangeSelector: React.FC<Props> = ({ children, dateRange, dateRangeType, onChange }) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [dropdownVisible, setDropdownVisibility] = useState(false);

  const handleChangeDropDownVisibility = useCallback(
    (dropdownVisible: boolean) => {
      setDropdownVisibility(dropdownVisible);
      if (dropdownVisible) {
        analytics.clicked(["Change Date"]);
      }
    },
    [analytics]
  );

  const [newDateRangeType, setNewDateRangeType] =
    useState<StandardAndCustomRangeType>(dateRangeType);
  const [newDateRange, setNewDateRange] = useState<DateRange | undefined>(dateRange);
  useEffect(() => setNewDateRange(dateRange), [dateRange]);
  useEffect(() => setNewDateRangeType(dateRangeType), [dateRangeType]);

  const handleChangeDateRange = useCallback(
    (dateRange: DateRange) => {
      analytics.clicked(["Change Date"], dateRange);
      setNewDateRange(dateRange);
    },
    [analytics]
  );

  const handleChangeRangeType = useCallback(
    (value: Exclude<DateRangeType, DateRangeType.ALL>) => {
      analytics.clicked(["Select Range", value]);
      setNewDateRangeType(value === DateRangeType.CUSTOM ? value : `${value}0`);
    },
    [analytics]
  );

  const handleApplyClick = useCallback(() => {
    const dateRange =
      newDateRangeType === DateRangeType.CUSTOM
        ? newDateRange
        : standardRangeToDateRange(newDateRangeType);

    if (!dateRange) {
      return;
    }
    onChange?.({ dateRange, dateRangeType: newDateRangeType });
    setDropdownVisibility(false);
    analytics.clicked(["Change Date Apply"], newDateRange);
  }, [analytics, onChange, newDateRange, newDateRangeType]);

  const handleCancelClick = useCallback(() => {
    setDropdownVisibility(false);
    // reset internal state
    setNewDateRangeType(dateRangeType);
    setNewDateRange(dateRange);
  }, [dateRangeType, dateRange]);

  return (
    <Dropdown
      onOpenChange={handleChangeDropDownVisibility}
      open={dropdownVisible}
      overlay={
        <div className={styles.container}>
          <div className={styles.title}>
            {intl.formatMessage({
              id: "dashboard.sidebar.dateRangeSelector.rangeType.label",
              defaultMessage: "Stack Rank Date Range",
              description:
                "A range type selectbox title in Date Range dropdown on the Dashboard page",
            })}
          </div>
          <Row gutter={[layout.spacerS, layout.spacerM]}>
            <Col span={24}>
              <RangeTypeSelect
                onChange={handleChangeRangeType}
                value={parseRange(newDateRangeType)?.dateRangeType ?? DateRangeType.WEEK}
              />
            </Col>
            <Col span={24}>
              <Range
                dateRange={newDateRange}
                dateRangeType={newDateRangeType}
                onChangeDateRange={handleChangeDateRange}
                onChangeDateRangeType={setNewDateRangeType}
              />
            </Col>
            <Col span={24}>
              <div className={styles.note}>
                {intl.formatMessage({
                  id: "dashboard.sidebar.dateRangeSelector.note",
                  defaultMessage:
                    "Please note that rank comparisons against the previous period are disabled when viewing a custom time range.",
                  description: "Note on dashboard stack rank time range selector",
                })}
              </div>
            </Col>
            <Col span={24}>
              <Row gutter={layout.spacerS} justify="end">
                <Col>
                  <Button onClick={handleCancelClick}>
                    {intl.formatMessage({
                      id: "dashboard.sidebar.dateRangeSelector.cancel",
                      defaultMessage: "Cancel",
                      description:
                        "Cancel button title in Dashboard Stack Rank's date range selector",
                    })}
                  </Button>
                </Col>
                <Col>
                  <Button onClick={handleApplyClick} type="primary">
                    {intl.formatMessage({
                      id: "dashboard.sidebar.dateRangeSelector.apply",
                      defaultMessage: "Apply",
                      description:
                        "Apply button title in Dashboard Stack Rank's date range selector",
                    })}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      }
      trigger={["click"]}
    >
      {children}
    </Dropdown>
  );
};

export default DateRangeSelector;
